import { Operation } from '@apollo/client'

import { requestIdHeader } from './headers'

export const getOperationErrorProperties = (operation: Operation) => {
  if (!operation) return {}

  const requestId = operation?.getContext()?.headers?.[requestIdHeader]
  return {
    requestId,
    operation: operation?.operationName,
    query: operation?.query?.loc?.source?.body,
    variables: operation?.variables
  }
}

// Apollo funnels BOTH genuine transport failures and non-2xx HTTP responses
// into `networkError`. A non-2xx arrives as a `ServerError`/`ServerParseError`
// carrying `statusCode`, so hardcoding `status: 'network'` collapsed edge
// rejections (e.g. a WAF 403), gateway 5xx, offline clients and blocked CORS
// preflights into one `networkError:<op>:network` fingerprint.
//
// Resolving a real status splits those into actionable buckets and reads
// consistently with the `apiError`/`serverError` fingerprints, which already
// use the HTTP status as their third segment.
//
// `statusCode` is narrowed with `in`/`typeof` rather than asserted: this is a
// runtime error object whose shape we cannot prove, and `ServerError`'s export
// path moves between Apollo versions, so importing the type is its own churn.
//
// A blocked CORS preflight is opaque by design — `fetch` rejects with a bare
// TypeError carrying no status or headers — so it cannot be distinguished from
// other connectivity loss here and lands in `unreachable`.
export const getTransportErrorProperties = (networkError: Error) => {
  const statusCode =
    'statusCode' in networkError && typeof networkError.statusCode === 'number'
      ? networkError.statusCode
      : undefined
  const isOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false

  return {
    errorStack: networkError?.stack,
    errorName: networkError?.name,
    status: statusCode ?? (isOffline ? 'offline' : 'unreachable'),
    online: !isOffline
  }
}
